/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useEffect, useRef, useState, useCallback } from 'react';

export const useSyncedHeight = (enabled: boolean) => {
  const [syncedHeight, setSyncedHeight] = useState<number | null>(null);
  const ref1 = useRef<HTMLElement>(null);
  const ref2 = useRef<HTMLElement>(null);

  const updateHeight = useCallback(() => {
    if (!enabled || !ref1.current || !ref2.current) {
      setSyncedHeight(null);
      return;
    }

    ref1.current.style.height = 'auto';
    ref2.current.style.height = 'auto';

    const height1 = ref1.current.offsetHeight;
    const height2 = ref2.current.offsetHeight;
    const maxHeight = Math.max(height1, height2);

    ref1.current.style.height = '';
    ref2.current.style.height = '';

    setSyncedHeight(maxHeight);
  }, [enabled]);

  useEffect(() => {
    updateHeight();
  }, [updateHeight]);

  useEffect(() => {
    if (!enabled) return;

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });

    if (ref1.current) resizeObserver.observe(ref1.current);
    if (ref2.current) resizeObserver.observe(ref2.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enabled, updateHeight]);

  useEffect(() => {
    const handleResize = () => {
      updateHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateHeight]);

  return { ref1, ref2, syncedHeight };
};
